/**
 *
 * WalletDetailsPage
 *
 */

// @ts-nocheck

import React, { memo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';

import {
  Icon,
  Space,
  Text,
  Button,
  toast
} from '@cashfree-intl/coherent';

import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';

import EVENTS from 'constants/analytics';
import Analytics from 'utils/analytics';
import {
  exportToCSV as exportToCSVFn,
  getItemFromLocalStorage,
  titleCase,
} from 'utils/common';
import DashboardFilters from 'containers/DashboardFilters';
import {
  CURRENCY_SYMBOL,
  DATE_AND_TIME_FORMAT,
  DEFAULT_CURRENT_PAGE,
  DEFAULT_LIMIT_OPTIONS,
  PLACEHOLDER_TEXT,
} from 'utils/constants';
import Loader from 'components/Loader';
import moment from 'moment';
import makeSelectWalletDetailsPage from './selectors';
import reducer from './reducer';
import saga from './saga';
import WalletDetailsCard from './components/WalletDetailsCard';
import {
  addMoneyToWalletAction,
  disableLoaderAction,
  enableLoaderAction,
  exportToCSVAction,
  fetchWalletHistoryDataAction,
  getWalletBalanceAction,
  getWalletStatusAction,
  modifyWalletStatusAction,
  resetFiltersAction,
  resetPageAction,
  updateFiltersAction,
  updateWalletBalanceAction,
  updateWalletStatusAction,
} from './actions';
import {
  FEE,
  INVOICINGSVC,
  PG_REFUND,
  WALLET_TABLE_HEADINGS,
} from './constants';
import { StyledCursorText } from './styled';
import { getWalletBankList } from 'services/pgConfig';
import PaginatedTable from 'components/PaginatedTable';
import { formatColumns } from 'components/PaginatedTable/helpers';

export function WalletDetailsPage({
  getWalletStatus,
  getWalletBalance,
  addMoneyToWallet,
  modifyWalletStatus,
  fetchWalletHistoryData,
  enableLoader,
  disableLoader,
  updateFilters,
  resetFilters,
  resetPage,
  exportToCSV,
  updateWalletBalance,
  updateWalletStatus,
  walletDetailsPage: {
    filters,
    filtersForDisplay,
    walletData,
    walletBalance,
    isLoading,
    bankDetails,
  },
  
}) {
  useInjectReducer({ key: 'walletDetailsPage', reducer });
  useInjectSaga({ key: 'walletDetailsPage', saga, mode: '' });

  const [dataForExport, setDataForExport] = useState({});
  const [bankList,setBankList]=useState([])

  const merchantId = getItemFromLocalStorage('merchantId');

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [tableData, updateTableData] = useState<any>({});

  const formatTableData = data => {
    let formattedData = [];
    const { content = [] } = data;

    formattedData = content.map(rowData => ({
      ...rowData,
      id: rowData.id,
      txTime: moment(rowData.addedOn).format(DATE_AND_TIME_FORMAT),
      eventType: () => (
        <span>{titleCase(rowData.event || PLACEHOLDER_TEXT)}</span>
      ),
      refundId: () => <span>{rowData.eventId}</span>,
      utr: () => <span>{rowData.utr || PLACEHOLDER_TEXT}</span>,
      saleType: () => (
        <span>{titleCase(rowData.saleType || PLACEHOLDER_TEXT)}</span>
      ),
      amount: () => (
        <Text color={rowData.saleType === 'CREDIT' ? 'success' : 'danger'}>
          {CURRENCY_SYMBOL.INR} {rowData.amount}
        </Text>
      ),
      closingBalance: () => (
        <span>
          {CURRENCY_SYMBOL.INR} {rowData.closingBalance}
        </span>
      ),
    }));

    const hasPrev = filters.pageNo !== 1;
    const hasNext = content.length >= filters.pageSize;

    updateTableData({
      currentPageRowData: formattedData,
      hasPrev,
      hasNext,
    });
    disableLoader();
  };

  const getTableData = () => {
    enableLoader();
    const { appliedFilters, pageNo, pageSize } = filters;
    const { startDate, endDate, ...restAppliedFilters } = appliedFilters;

    let formattedFilterObj = {
      startDateTime: startDate,
      endDateTime: endDate,
      ...restAppliedFilters,
      pageNo: pageNo - 1,
      pageSize,
    };

    let tenant = PG_REFUND;

    switch (walletType) {
      case 'dispute':
        tenant = 'pg';
        break;
      case FEE:
        tenant = INVOICINGSVC;
        break;
      default:
        break;
    }

    formattedFilterObj = { ...formattedFilterObj, tenant };

    if (walletType === 'dispute') {
      formattedFilterObj = { ...formattedFilterObj, beneType: 'merchant' };
    }

    if (
      appliedFilters.search &&
      appliedFilters.search.type &&
      appliedFilters.search.value &&
      appliedFilters.search.value.trim()
    ) {
      const {
        search: { type, value },
      } = appliedFilters;
      formattedFilterObj[type] = value.trim();
      delete formattedFilterObj.search;
    }

    setDataForExport({
      options: {
        ...formattedFilterObj,
        isDownloadRequest: true,
      },
      cb: exportToCSVFn,
      feeWallet: walletType === FEE,
    });

    fetchWalletHistoryData({
      options: formattedFilterObj,
      cb: formatTableData,
      walletType,
    });
  };

  let { walletType = '' } = Object.fromEntries(searchParams);
  walletType = walletType.substring(0, walletType.indexOf('Wallet'));

  const navigateBack = () => navigate(-1);

  const getWalletInfo = () => {
    enableLoader();
    getWalletStatus({ walletType });
    if (walletType !== FEE) getWalletBalance({ walletType });
  };

  const getBankLists = async () => {
    try {
      const response = await getWalletBankList();
      setBankList(response);
    } catch (error) {
      toast.error(error);
      setBankList([]);
    }
  };

  useEffect(() => {
    getWalletInfo();
    getBankLists()
  }, [searchParams.get('env')]);

  useEffect(() => {
    getTableData();
  }, [filters, searchParams.get('env')]);

  const handleRefreshClick = wallet => {
    Analytics.track(EVENTS.WALLETS.REFUND_WALLET.REFRESH_WALLET_BALANCE);
    updateWalletBalance({});
    enableLoader();
    getWalletBalance({ walletType: wallet });
  };

  const handleFeeWalletRefreshClick = () => {
    updateWalletStatus({});
    getWalletStatus({ feeWallet: walletType === FEE });
  };

  const addMoneyClickHandler = () => {
    Analytics.track(EVENTS.WALLETS.REFUND_WALLET.ADD_MONEY);
    enableLoader();
    addMoneyToWallet({ walletType });
  };

  const handleToggleChange = toggleStatus => {
    Analytics.track(EVENTS.WALLETS.REFUND_WALLET.UPDATE_WALLET_STATUS);
    enableLoader();
    modifyWalletStatus({
      updatedBy: merchantId,
      name: walletType,
      status: toggleStatus,
    });
  };

  const getTableDataForExportToCSV = () => {
    enableLoader();
    exportToCSV(dataForExport);
  };

  const renderTabCTA = () => (
    <Button
      primary
      onClick={getTableDataForExportToCSV}
      className="cta-item"
      disabled={currentPageRowData.length === 0 && !hasPrev}
    >
      Export
    </Button>
  );

  const {
    currentPageRowData = [],
    hasPrev = false,
    hasNext = false,
  } = tableData;

  const onPageChange = type => {
    const options = { ...filters, filtersForDisplay };
    switch (type) {
      case 'NEXT':
        options.pageNo += 1;
        break;
      case 'PREV':
        options.pageNo -= 1;
        break;
      default:
        break;
    }
    updateFilters(options);
  };

  const onLimitChange = (e, limitChangeData) => {
    const options = {
      ...filters,
      pageNo: DEFAULT_CURRENT_PAGE,
      pageSize: parseInt(limitChangeData.value, 10),
      filtersForDisplay,
    };
    updateFilters(options);
  };

  const FORMATTED_COLUMN_ID = formatColumns(WALLET_TABLE_HEADINGS);
  const renderTable = () => (
    <PaginatedTable
        data={currentPageRowData}
        tableHeading={FORMATTED_COLUMN_ID}
        limit={filters.pageSize}
        limitOptions={DEFAULT_LIMIT_OPTIONS}
        hasPrev={hasPrev}
        hasNext={hasNext}
        currentPage={filters.pageNo}
        currentPageCount={10}
        onPageChange={onPageChange}
        onLimitChange={onLimitChange}
        fetching={isLoading}
      />
  );

  return (
    <>
      <Loader active={isLoading} />
      <Space alignItems="center" gap={2} className="mt-4">
        <Icon name="chevron-left" />
        <StyledCursorText variant="b14" color="primary" onClick={navigateBack}>
          Back
        </StyledCursorText>
        <Text variant="h16">{titleCase(walletType)} Wallet</Text>
      </Space>
      <Text variant="b14" className="mt-4 mb-3">
        You can view your wallet transactions and recharge account details of
        your wallet in this screen.
      </Text>
      <WalletDetailsCard
        walletType={walletType}
        walletData={walletData}
        walletBalance={
          walletType === FEE ? { balance: walletData?.balance } : walletBalance
        }
        handleRefreshClick={
          walletType === FEE ? handleFeeWalletRefreshClick : handleRefreshClick
        }
        getWalletInfo={getWalletInfo}
        addMoneyClickHandler={addMoneyClickHandler}
        handleToggleChange={handleToggleChange}
        bankDetails={bankDetails}
        bankList={bankList}
      />
      <Text variant="h16" className="mt-4 mb-3">
        Wallet History
      </Text>
      <div className="dashboard-filters-wrapper">
        
        <DashboardFilters
          menuSelected="Wallets"
          filters={filters}
          filtersForDisplay={filtersForDisplay}
          activeNavigation={walletType}
          getTableDataFn={getTableData}
          resetFilterFn={resetFilters}
          resetPageFn={resetPage}
          updateFilterFn={updateFilters}
        />
        <div className="tab-cta-wrapper">{renderTabCTA()}</div>
      </div>
      <div >{renderTable()}</div>
    </>
  );
}

WalletDetailsPage.propTypes = {
  getWalletStatus: PropTypes.func.isRequired,
  getWalletBalance: PropTypes.func.isRequired,
  addMoneyToWallet: PropTypes.func.isRequired,
  modifyWalletStatus: PropTypes.func.isRequired,
  fetchWalletHistoryData: PropTypes.func.isRequired,
  enableLoader: PropTypes.func.isRequired,
  disableLoader: PropTypes.func.isRequired,
  updateFilters: PropTypes.func.isRequired,
  resetFilters: PropTypes.func.isRequired,
  resetPage: PropTypes.func.isRequired,
  walletDetailsPage: PropTypes.object.isRequired,
  exportToCSV: PropTypes.func.isRequired,
  updateWalletBalance: PropTypes.func.isRequired,
  updateWalletStatus: PropTypes.func.isRequired,
};

const mapStateToProps = createStructuredSelector({
  walletDetailsPage: makeSelectWalletDetailsPage(),
});

export function mapDispatchToProps(dispatch) {
  return {
    getWalletStatus: data => dispatch(getWalletStatusAction(data)),
    getWalletBalance: data => dispatch(getWalletBalanceAction(data)),
    addMoneyToWallet: data => dispatch(addMoneyToWalletAction(data)),
    modifyWalletStatus: data => dispatch(modifyWalletStatusAction(data)),
    fetchWalletHistoryData: data =>
      dispatch(fetchWalletHistoryDataAction(data)),
    enableLoader: () => dispatch(enableLoaderAction()),
    disableLoader: () => dispatch(disableLoaderAction()),
    updateFilters: data => dispatch(updateFiltersAction(data)),
    resetFilters: () => dispatch(resetFiltersAction()),
    resetPage: data => dispatch(resetPageAction(data)),
    exportToCSV: data => dispatch(exportToCSVAction(data)),
    updateWalletBalance: data => dispatch(updateWalletBalanceAction(data)),
    updateWalletStatus: data => dispatch(updateWalletStatusAction(data)),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
)(WalletDetailsPage);
